import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { dsWikiService } from '../services';

interface TableCell {
  content: string;
  isHeader?: boolean;
}

interface ImageData {
  src: string;
  alt: string;
  caption?: string;
  alignment: 'left' | 'center' | 'right' | 'full';
}

interface ContentBlock {
  id: string;
  type: 'h1' | 'h2' | 'h3' | 'paragraph' | 'list' | 'code' | 'quote' | 'divider' | 'table' | 'image';
  content: string;
  items?: string[];
  tableData?: TableCell[][];
  imageData?: ImageData;
}

interface ArticleDetailProps {}

export default function ArticleDetail(props: ArticleDetailProps) {
  const { articleId } = useParams<{ articleId: string }>();
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [currentSection, setCurrentSection] = useState('');
  const [readingProgress, setReadingProgress] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isTocCollapsed, setIsTocCollapsed] = useState(false);
  const [activePanel, setActivePanel] = useState<'related' | 'versioning' | 'analytics' | 'contributors' | 'ratings' | 'suggestions' | null>(null);
  const [tocSearchQuery, setTocSearchQuery] = useState('');
  const [userRating, setUserRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [ratingComment, setRatingComment] = useState('');
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);
  const [suggestionType, setSuggestionType] = useState<'correction' | 'addition' | 'removal' | 'general'>('general');
  const [suggestionSection, setSuggestionSection] = useState('');
  const [suggestionText, setSuggestionText] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  // API loading states
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // Article data - will be fetched from API when available
  // TODO: Call Article detail API when available
  // For now, show empty state indicating the API is not yet available
  const article = {
    id: articleId,
    title: 'Article Not Available',
    subtitle: 'The Article API is not yet implemented',
    summary: 'Article data will be loaded from the backend API once it is available.',
    blocks: [] as ContentBlock[],
    sections: [] as { id: string; title: string; level: number; subsections?: { id: string; title: string; level: number }[] }[],
    author: {
      name: 'Unknown',
      title: '',
      role: 'Author'
    },
    status: 'draft',
    topic: '',
    publishedDate: '',
    lastUpdated: '',
    readTime: '0 min read',
    views: 0,
    tags: [] as string[]
  };

  // Related articles data - will be populated from API when available
  const relatedArticles: { id: number; title: string; desc: string; views: number }[] = [];

  // Version history data - will be populated from API when available
  const versions: { version: string; date: string; author: string; changes: string }[] = [];

  // Analytics data - will be populated from API when available
  const analytics = {
    totalViews: 0,
    uniqueVisitors: 0,
    avgReadTime: '0 min',
    bounceRate: '0%',
    weeklyViews: [] as number[],
    topReferrers: [] as string[]
  };

  // Contributors data - will be populated from API when available
  const contributors: { name: string; role: string; avatar: string; contributions: number }[] = [];

  const togglePanel = (panel: 'related' | 'versioning' | 'analytics' | 'contributors' | 'ratings' | 'suggestions') => {
    setActivePanel(activePanel === panel ? null : panel);
  };

  // Ratings data - will be populated from API when available
  const articleRating = {
    average: 0,
    totalRatings: 0,
    distribution: [] as { stars: number; count: number; percentage: number }[]
  };

  const recentRatings: { user: string; avatar: string; rating: number; comment: string; date: string }[] = [];

  const handleSubmitRating = () => {
    if (userRating > 0) {
      // TODO: Call rating API when available
      console.log('Submitting rating:', { rating: userRating, comment: ratingComment });
      alert('Rating API is not yet available. Rating data logged to console.');
      setShowRatingForm(false);
      setRatingComment('');
    }
  };

  // Change suggestions data - will be populated from API when available
  const changeSuggestions: {
    id: number;
    user: string;
    avatar: string;
    type: 'correction' | 'addition' | 'removal' | 'general';
    section: string;
    suggestion: string;
    status: 'pending' | 'approved' | 'rejected';
    date: string;
    votes: number;
  }[] = [];

  const handleSubmitSuggestion = () => {
    if (suggestionText.trim()) {
      // TODO: Call suggestion API when available
      console.log('Submitting suggestion:', { type: suggestionType, section: suggestionSection, text: suggestionText });
      alert('Suggestion API is not yet available. Suggestion data logged to console.');
      setShowSuggestionForm(false);
      setSuggestionType('general');
      setSuggestionSection('');
      setSuggestionText('');
    }
  };

  const getSuggestionTypeColor = (type: string) => {
    switch (type) {
      case 'correction': return 'bg-red-100 text-red-700 border-red-200';
      case 'addition': return 'bg-green-100 text-green-700 border-green-200';
      case 'removal': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const getSuggestionTypeIcon = (type: string) => {
    switch (type) {
      case 'correction': return 'ri-edit-2-line';
      case 'addition': return 'ri-add-circle-line';
      case 'removal': return 'ri-delete-bin-line';
      default: return 'ri-lightbulb-line';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-yellow-500';
    }
  };

  // Reference to the scrollable content area
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle scroll events for reading progress and scrollspy
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      setReadingProgress(Math.min(progress, 100));

      setShowBackToTop(scrollTop > 300);

      // Find all headings within the content
      const headings = scrollContainer.querySelectorAll('h1[id], h2[id], h3[id]');
      let current = '';
      let closestHeading = '';
      let closestDistance = Infinity;

      headings.forEach((heading) => {
        const rect = heading.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;

        // Track heading that's past the top threshold
        if (relativeTop <= 120) {
          current = heading.id;
        }

        // Also track the closest heading to the top for better UX
        const distance = Math.abs(relativeTop - 80);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestHeading = heading.id;
        }
      });

      // Use current (past threshold) or closest heading
      const activeHeading = current || closestHeading;

      if (activeHeading !== currentSection) {
        setCurrentSection(activeHeading);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [currentSection]);

  // Auto-scroll TOC to keep active item visible
  useEffect(() => {
    if (currentSection) {
      const tocItem = document.getElementById(`toc-${currentSection}`);
      if (tocItem) {
        tocItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [currentSection]);

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element && scrollContainerRef.current) {
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop = scrollContainerRef.current.scrollTop;
      const offsetTop = elementRect.top - containerRect.top + scrollTop - 80; // 80px offset from top

      scrollContainerRef.current.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
        <div
          className="h-full bg-blue-600 transition-all duration-300"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Sticky Breadcrumb Header */}
      <div className="sticky top-1 z-40 module-header shadow-lg">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm">
              <Link to="/app/ds-wiki" className="text-white/80 hover:text-white flex items-center">
                <i className="ri-home-4-line mr-1"></i>
                Knowledge Share
              </Link>
              <i className="ri-arrow-right-s-line mx-2 text-white/60"></i>
              <Link to="/app/ds-wiki/articles" className="text-white/80 hover:text-white">
                Articles
              </Link>
              <i className="ri-arrow-right-s-line mx-2 text-white/60"></i>
              <span className="bg-white/20 text-white px-3 py-1 rounded-full font-medium truncate max-w-md">
                {article.title}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Link
                to={`/app/ds-wiki/articles/${articleId}/edit`}
                className="bg-white/20 text-white px-3 py-1.5 rounded-lg hover:bg-white/30 flex items-center text-sm font-medium transition-colors"
              >
                <i className="ri-edit-line mr-1"></i>
                Edit
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-57px)]">
        {/* Left Sidebar - Table of Contents (Collapsible with own scroll) */}
        <div className={`flex-shrink-0 border-r border-gray-200 bg-white transition-all duration-300 ${
          isTocCollapsed ? 'w-14' : 'w-72'
        }`}>
          <div className="h-full flex flex-col">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                {!isTocCollapsed && (
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <i className="ri-list-unordered mr-2 text-blue-600"></i>
                    Table of Contents
                  </h3>
                )}
                <button
                  onClick={() => setIsTocCollapsed(!isTocCollapsed)}
                  className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${isTocCollapsed ? 'mx-auto' : ''}`}
                  title={isTocCollapsed ? 'Expand' : 'Collapse'}
                >
                  <i className={`ri-${isTocCollapsed ? 'menu-unfold-line' : 'menu-fold-line'} text-gray-600`}></i>
                </button>
              </div>
              {/* TOC Search */}
              {!isTocCollapsed && (
                <div className="relative">
                  <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="text"
                    value={tocSearchQuery}
                    onChange={(e) => setTocSearchQuery(e.target.value)}
                    placeholder="Search sections..."
                    className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {tocSearchQuery && (
                    <button
                      onClick={() => setTocSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  )}
                </div>
              )}
            </div>
            {!isTocCollapsed ? (
              <nav className="flex-1 overflow-y-auto p-4 space-y-1">
                {article.sections
                  .filter(section => {
                    if (!tocSearchQuery.trim()) return true;
                    const query = tocSearchQuery.toLowerCase();
                    const matchesTitle = section.title.toLowerCase().includes(query);
                    const matchesSubsections = section.subsections?.some((sub: any) =>
                      sub.title.toLowerCase().includes(query)
                    );
                    return matchesTitle || matchesSubsections;
                  })
                  .map((section) => {
                  // Check if current section or any of its subsections is active
                  const isParentActive = currentSection === section.id;
                  const hasActiveChild = section.subsections?.some((sub: any) => sub.id === currentSection);
                  const isHighlighted = isParentActive || hasActiveChild;

                  return (
                    <div key={section.id}>
                      <button
                        id={`toc-${section.id}`}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 border-l-4 ${
                          isParentActive
                            ? 'bg-blue-100 text-blue-700 font-semibold border-blue-500'
                            : hasActiveChild
                            ? 'bg-blue-50 text-blue-600 font-medium border-blue-300'
                            : 'text-gray-600 border-transparent hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600'
                        }`}
                      >
                        <i className={`ri-bookmark-line mr-2 ${isHighlighted ? 'text-blue-500' : 'text-gray-400'}`}></i>
                        {section.title}
                      </button>
                      {section.subsections && section.subsections.length > 0 && (
                        <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-200">
                          {section.subsections
                            .filter((subsection: any) => {
                              if (!tocSearchQuery.trim()) return true;
                              return subsection.title.toLowerCase().includes(tocSearchQuery.toLowerCase());
                            })
                            .map((subsection: any) => (
                              <button
                                key={subsection.id}
                                id={`toc-${subsection.id}`}
                                onClick={() => scrollToSection(subsection.id)}
                                className={`w-full text-left px-3 py-1.5 text-xs rounded-r-lg transition-all duration-200 -ml-0.5 border-l-2 ${
                                  currentSection === subsection.id
                                    ? 'bg-blue-100 text-blue-700 font-semibold border-blue-500'
                                    : 'text-gray-500 border-transparent hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600'
                                }`}
                              >
                                {subsection.title}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            ) : (
              <div className="flex-1 overflow-y-auto py-4">
                {article.sections.map((section, index) => {
                  const isParentActive = currentSection === section.id;
                  const hasActiveChild = section.subsections?.some((sub: any) => sub.id === currentSection);
                  const isHighlighted = isParentActive || hasActiveChild;

                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full p-3 flex justify-center transition-all duration-200 ${
                        isHighlighted
                          ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-500'
                          : 'text-gray-500 hover:bg-gray-50 border-l-4 border-transparent'
                      }`}
                      title={section.title}
                    >
                      <span className="font-medium">{index + 1}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Main Content Area (Scrollable) */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-6">
            {/* Top Action Bar with Group Buttons */}
            <div className="flex items-center justify-end mb-4">
              <div className="inline-flex bg-white rounded-lg border border-gray-200 shadow-sm p-1">
                <button
                  onClick={() => togglePanel('related')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center ${
                    activePanel === 'related'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-links-line mr-2"></i>
                  Related
                </button>
                <button
                  onClick={() => togglePanel('versioning')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center ${
                    activePanel === 'versioning'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-history-line mr-2"></i>
                  Versions
                </button>
                <button
                  onClick={() => togglePanel('analytics')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center ${
                    activePanel === 'analytics'
                      ? 'bg-green-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-bar-chart-line mr-2"></i>
                  Analytics
                </button>
                <button
                  onClick={() => togglePanel('contributors')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center ${
                    activePanel === 'contributors'
                      ? 'bg-orange-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-team-line mr-2"></i>
                  Contributors
                </button>
                <button
                  onClick={() => togglePanel('ratings')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center ${
                    activePanel === 'ratings'
                      ? 'bg-yellow-500 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-star-line mr-2"></i>
                  <span className="flex items-center">
                    {articleRating.average.toFixed(1)}
                    <i className="ri-star-fill text-yellow-500 ml-1 text-xs"></i>
                    <span className="text-gray-400 ml-1">({articleRating.totalRatings})</span>
                  </span>
                </button>
                <button
                  onClick={() => togglePanel('suggestions')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center ${
                    activePanel === 'suggestions'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-lightbulb-line mr-2"></i>
                  Suggestions
                  <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                    activePanel === 'suggestions' ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {changeSuggestions.filter(s => s.status === 'pending').length}
                  </span>
                </button>
              </div>
            </div>

            {/* Expandable Panels */}
            {activePanel && (
              <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-in slide-in-from-top-2 duration-200">
                {/* Related Articles Panel */}
                {activePanel === 'related' && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center">
                        <i className="ri-links-line mr-2 text-blue-600"></i>
                        Related Articles
                      </h3>
                      <button onClick={() => setActivePanel(null)} className="text-gray-400 hover:text-gray-600">
                        <i className="ri-close-line text-xl"></i>
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {relatedArticles.map((item, index) => (
                        <Link
                          key={item.id}
                          to={`/app/ds-wiki/articles/${item.id}`}
                          className="flex items-center p-4 rounded-lg border-2 border-transparent bg-gray-50 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200"
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold mr-3 ${
                            index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-purple-500' : 'bg-indigo-500'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{item.title}</p>
                            <p className="text-xs text-gray-500">{item.desc}</p>
                            <p className="text-xs text-gray-400 mt-1">{item.views.toLocaleString()} views</p>
                          </div>
                          <i className="ri-arrow-right-s-line text-gray-400"></i>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Versioning Panel */}
                {activePanel === 'versioning' && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center">
                        <i className="ri-history-line mr-2 text-purple-600"></i>
                        Version History
                      </h3>
                      <button onClick={() => setActivePanel(null)} className="text-gray-400 hover:text-gray-600">
                        <i className="ri-close-line text-xl"></i>
                      </button>
                    </div>
                    <div className="space-y-3">
                      {versions.map((v, index) => (
                        <div
                          key={v.version}
                          className={`flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                            index === 0
                              ? 'bg-purple-50 border-purple-200'
                              : 'bg-gray-50 border-transparent hover:border-purple-200 hover:bg-purple-50'
                          }`}
                        >
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${
                            index === 0 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'
                          }`}>
                            <span className="font-bold text-sm">v{v.version}</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900">{v.changes}</p>
                              {index === 0 && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Current</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              <span className="font-medium">{v.author}</span> • {new Date(v.date).toLocaleDateString()}
                            </p>
                          </div>
                          <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analytics Panel */}
                {activePanel === 'analytics' && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center">
                        <i className="ri-bar-chart-line mr-2 text-green-600"></i>
                        Article Analytics
                      </h3>
                      <button onClick={() => setActivePanel(null)} className="text-gray-400 hover:text-gray-600">
                        <i className="ri-close-line text-xl"></i>
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                        <div className="text-2xl font-bold text-green-700">{analytics.totalViews.toLocaleString()}</div>
                        <div className="text-sm text-green-600">Total Views</div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                        <div className="text-2xl font-bold text-blue-700">{analytics.uniqueVisitors.toLocaleString()}</div>
                        <div className="text-sm text-blue-600">Unique Visitors</div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                        <div className="text-2xl font-bold text-purple-700">{analytics.avgReadTime}</div>
                        <div className="text-sm text-purple-600">Avg. Read Time</div>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-4 border-2 border-orange-200">
                        <div className="text-2xl font-bold text-orange-700">{analytics.bounceRate}</div>
                        <div className="text-sm text-orange-600">Bounce Rate</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Weekly Views</h4>
                        <div className="flex items-end space-x-2 h-20">
                          {analytics.weeklyViews.map((views, i) => (
                            <div
                              key={i}
                              className="flex-1 bg-green-500 rounded-t hover:bg-green-600 transition-colors"
                              style={{ height: `${(views / Math.max(...analytics.weeklyViews)) * 100}%` }}
                              title={`${views} views`}
                            />
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 mt-2">
                          <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Top Referrers</h4>
                        <div className="space-y-2">
                          {analytics.topReferrers.map((ref, i) => (
                            <div key={ref} className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">{ref}</span>
                              <span className="text-sm font-medium text-gray-900">{Math.round(100 - i * 20)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contributors Panel */}
                {activePanel === 'contributors' && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center">
                        <i className="ri-team-line mr-2 text-orange-600"></i>
                        Contributors
                      </h3>
                      <button onClick={() => setActivePanel(null)} className="text-gray-400 hover:text-gray-600">
                        <i className="ri-close-line text-xl"></i>
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      {contributors.map((person, index) => (
                        <div
                          key={person.name}
                          className="flex flex-col items-center p-4 rounded-lg border-2 border-transparent bg-gray-50 hover:bg-orange-50 hover:border-orange-300 transition-all duration-200 cursor-pointer"
                        >
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg mb-2 ${
                            index === 0 ? 'bg-orange-500' : index === 1 ? 'bg-blue-500' : index === 2 ? 'bg-purple-500' : 'bg-green-500'
                          }`}>
                            {person.avatar}
                          </div>
                          <p className="text-sm font-medium text-gray-900 text-center">{person.name}</p>
                          <p className="text-xs text-gray-500">{person.role}</p>
                          <p className="text-xs text-orange-600 mt-1">{person.contributions} contributions</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ratings Panel */}
                {activePanel === 'ratings' && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center">
                        <i className="ri-star-line mr-2 text-yellow-500"></i>
                        Ratings & Reviews
                      </h3>
                      <button onClick={() => setActivePanel(null)} className="text-gray-400 hover:text-gray-600">
                        <i className="ri-close-line text-xl"></i>
                      </button>
                    </div>

                    <div className="grid grid-cols-3 gap-6">
                      {/* Rating Summary */}
                      <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-5 border border-yellow-200">
                        <div className="text-center mb-4">
                          <div className="text-5xl font-bold text-yellow-600">{articleRating.average.toFixed(1)}</div>
                          <div className="flex items-center justify-center mt-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <i
                                key={star}
                                className={`text-xl ${star <= Math.round(articleRating.average) ? 'ri-star-fill text-yellow-500' : 'ri-star-line text-gray-300'}`}
                              ></i>
                            ))}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{articleRating.totalRatings} ratings</p>
                        </div>

                        {/* Rating Distribution */}
                        <div className="space-y-2">
                          {articleRating.distribution.map((item) => (
                            <div key={item.stars} className="flex items-center gap-2">
                              <span className="text-xs text-gray-600 w-8">{item.stars} <i className="ri-star-fill text-yellow-500 text-[10px]"></i></span>
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-yellow-500 rounded-full transition-all duration-300"
                                  style={{ width: `${item.percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500 w-8">{item.count}</span>
                            </div>
                          ))}
                        </div>

                        {/* Rate This Article Button */}
                        <button
                          onClick={() => setShowRatingForm(!showRatingForm)}
                          className="w-full mt-4 px-4 py-2.5 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2"
                        >
                          <i className="ri-edit-line"></i>
                          {showRatingForm ? 'Cancel' : 'Rate This Article'}
                        </button>
                      </div>

                      {/* Rating Form or Recent Reviews */}
                      <div className="col-span-2">
                        {showRatingForm ? (
                          <div className="bg-white border border-gray-200 rounded-xl p-5">
                            <h4 className="font-semibold text-gray-900 mb-4">Submit Your Rating</h4>

                            {/* Star Selection */}
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating</label>
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    onMouseEnter={() => setHoverRating(star)}
                                    onMouseLeave={() => setHoverRating(0)}
                                    onClick={() => setUserRating(star)}
                                    className="p-1 transition-transform hover:scale-110"
                                  >
                                    <i
                                      className={`text-3xl ${
                                        star <= (hoverRating || userRating)
                                          ? 'ri-star-fill text-yellow-500'
                                          : 'ri-star-line text-gray-300'
                                      }`}
                                    ></i>
                                  </button>
                                ))}
                                <span className="ml-3 text-sm text-gray-600">
                                  {userRating > 0 ? `${userRating} star${userRating > 1 ? 's' : ''}` : 'Select rating'}
                                </span>
                              </div>
                            </div>

                            {/* Comment */}
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Comment (Optional)</label>
                              <textarea
                                value={ratingComment}
                                onChange={(e) => setRatingComment(e.target.value)}
                                placeholder="Share your thoughts about this article..."
                                rows={3}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-none"
                              />
                            </div>

                            {/* Submit Button */}
                            <button
                              onClick={handleSubmitRating}
                              disabled={userRating === 0}
                              className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                                userRating > 0
                                  ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              }`}
                            >
                              Submit Rating
                            </button>
                          </div>
                        ) : (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <i className="ri-chat-3-line mr-2 text-gray-500"></i>
                              Recent Reviews
                            </h4>
                            <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                              {recentRatings.map((review, index) => (
                                <div
                                  key={index}
                                  className="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:border-yellow-200 transition-colors"
                                >
                                  <div className="flex items-start gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                                      index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-purple-500' : index === 2 ? 'bg-green-500' : index === 3 ? 'bg-orange-500' : 'bg-pink-500'
                                    }`}>
                                      {review.avatar}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between">
                                        <span className="font-medium text-gray-900">{review.user}</span>
                                        <span className="text-xs text-gray-500">{new Date(review.date).toLocaleDateString()}</span>
                                      </div>
                                      <div className="flex items-center mt-1 mb-2">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <i
                                            key={star}
                                            className={`text-sm ${star <= review.rating ? 'ri-star-fill text-yellow-500' : 'ri-star-line text-gray-300'}`}
                                          ></i>
                                        ))}
                                      </div>
                                      <p className="text-sm text-gray-600">{review.comment}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Suggestions Panel */}
                {activePanel === 'suggestions' && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center">
                        <i className="ri-lightbulb-line mr-2 text-indigo-600"></i>
                        Change Suggestions
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowSuggestionForm(!showSuggestionForm)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                            showSuggestionForm
                              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              : 'bg-indigo-600 text-white hover:bg-indigo-700'
                          }`}
                        >
                          <i className={showSuggestionForm ? 'ri-close-line' : 'ri-add-line'}></i>
                          {showSuggestionForm ? 'Cancel' : 'Suggest a Change'}
                        </button>
                        <button onClick={() => setActivePanel(null)} className="text-gray-400 hover:text-gray-600">
                          <i className="ri-close-line text-xl"></i>
                        </button>
                      </div>
                    </div>

                    {showSuggestionForm ? (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 mb-4">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                          <i className="ri-edit-line mr-2 text-indigo-600"></i>
                          Submit Your Suggestion
                        </h4>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          {/* Suggestion Type */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Type of Change</label>
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                { value: 'correction', label: 'Correction', icon: 'ri-edit-2-line', color: 'red' },
                                { value: 'addition', label: 'Addition', icon: 'ri-add-circle-line', color: 'green' },
                                { value: 'removal', label: 'Removal', icon: 'ri-delete-bin-line', color: 'orange' },
                                { value: 'general', label: 'General', icon: 'ri-lightbulb-line', color: 'blue' }
                              ].map((type) => (
                                <button
                                  key={type.value}
                                  onClick={() => setSuggestionType(type.value as any)}
                                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 border ${
                                    suggestionType === type.value
                                      ? `bg-${type.color}-100 text-${type.color}-700 border-${type.color}-300`
                                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <i className={type.icon}></i>
                                  {type.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Section */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Related Section</label>
                            <select
                              value={suggestionSection}
                              onChange={(e) => setSuggestionSection(e.target.value)}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              <option value="">Select a section...</option>
                              {article.sections.map((section) => (
                                <option key={section.id} value={section.title}>{section.title}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Suggestion Text */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Your Suggestion</label>
                          <textarea
                            value={suggestionText}
                            onChange={(e) => setSuggestionText(e.target.value)}
                            placeholder="Describe the change you're suggesting in detail..."
                            rows={4}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                          />
                        </div>

                        {/* Submit Button */}
                        <button
                          onClick={handleSubmitSuggestion}
                          disabled={!suggestionText.trim()}
                          className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                            suggestionText.trim()
                              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          <i className="ri-send-plane-line mr-2"></i>
                          Submit Suggestion
                        </button>
                      </div>
                    ) : null}

                    {/* Suggestions List */}
                    <div className="space-y-3">
                      {/* Filter tabs */}
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-sm text-gray-500">Filter:</span>
                        <div className="flex gap-1">
                          {['all', 'pending', 'approved', 'rejected'].map((filter) => (
                            <button
                              key={filter}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                filter === 'all'
                                  ? 'bg-indigo-100 text-indigo-700'
                                  : filter === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : filter === 'approved'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {filter.charAt(0).toUpperCase() + filter.slice(1)}
                              {filter !== 'all' && (
                                <span className="ml-1">({changeSuggestions.filter(s => s.status === filter).length})</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Suggestions */}
                      <div className="max-h-80 overflow-y-auto pr-2 space-y-3">
                        {changeSuggestions.map((suggestion) => (
                          <div
                            key={suggestion.id}
                            className="bg-white border border-gray-200 rounded-xl p-4 hover:border-indigo-200 hover:shadow-sm transition-all"
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                                suggestion.status === 'approved' ? 'bg-green-500' :
                                suggestion.status === 'rejected' ? 'bg-red-500' : 'bg-indigo-500'
                              }`}>
                                {suggestion.avatar}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">{suggestion.user}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getSuggestionTypeColor(suggestion.type)}`}>
                                      <i className={`${getSuggestionTypeIcon(suggestion.type)} mr-1`}></i>
                                      {suggestion.type.charAt(0).toUpperCase() + suggestion.type.slice(1)}
                                    </span>
                                    <span className={`w-2 h-2 rounded-full ${getStatusColor(suggestion.status)}`} title={suggestion.status}></span>
                                  </div>
                                  <span className="text-xs text-gray-500">{new Date(suggestion.date).toLocaleDateString()}</span>
                                </div>
                                <p className="text-xs text-indigo-600 mb-1">
                                  <i className="ri-bookmark-line mr-1"></i>
                                  {suggestion.section}
                                </p>
                                <p className="text-sm text-gray-700 mb-3">{suggestion.suggestion}</p>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-indigo-600 transition-colors">
                                      <i className="ri-thumb-up-line"></i>
                                      <span>{suggestion.votes}</span>
                                    </button>
                                    <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                                      <i className="ri-chat-1-line"></i>
                                      Reply
                                    </button>
                                  </div>
                                  {suggestion.status === 'pending' && (
                                    <div className="flex items-center gap-2">
                                      <button className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors">
                                        <i className="ri-check-line mr-1"></i>Approve
                                      </button>
                                      <button className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors">
                                        <i className="ri-close-line mr-1"></i>Reject
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Main Article Content - Full Width */}
            <div>
            <article className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
              {/* Article Header */}
              <header className="mb-8">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                    {article.topic}
                  </span>
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                    {article.status}
                  </span>
                </div>

                <h1 className="text-4xl font-bold text-gray-900 mb-3">
                  {article.title}
                </h1>

                {article.subtitle && (
                  <p className="text-xl text-gray-600 mb-6">{article.subtitle}</p>
                )}

                <div className="flex items-center justify-between py-4 border-y border-gray-200">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                        {article.author.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{article.author.name}</div>
                        <div className="text-sm text-gray-500">{article.author.title}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <i className="ri-calendar-line mr-1"></i>
                      {new Date(article.publishedDate).toLocaleDateString()}
                    </span>
                    <span className="flex items-center">
                      <i className="ri-time-line mr-1"></i>
                      {article.readTime}
                    </span>
                    <span className="flex items-center">
                      <i className="ri-eye-line mr-1"></i>
                      {article.views.toLocaleString()}
                    </span>
                  </div>
                </div>
              </header>

              {/* Article Content - Block-based rendering */}
              <div ref={contentRef} className="prose prose-lg max-w-none">
                {article.blocks.map((block: ContentBlock) => {
                  switch (block.type) {
                    case 'h1':
                      return (
                        <h1 key={block.id} id={block.id} className="text-4xl font-bold text-gray-900 mt-10 mb-6 pb-3 border-b-2 border-blue-200">
                          {block.content}
                        </h1>
                      );
                    case 'h2':
                      return (
                        <h2 key={block.id} id={block.id} className="text-2xl font-bold text-blue-900 mt-10 mb-4 pb-2 border-b border-gray-200">
                          {block.content}
                        </h2>
                      );
                    case 'h3':
                      return (
                        <h3 key={block.id} id={block.id} className="text-xl font-semibold text-gray-800 mt-6 mb-3">
                          {block.content}
                        </h3>
                      );
                    case 'paragraph':
                      return (
                        <p key={block.id} className="text-gray-700 leading-relaxed mb-4">
                          {block.content}
                        </p>
                      );
                    case 'list':
                      return (
                        <ul key={block.id} className="list-disc ml-6 mb-4 space-y-2">
                          {block.items?.map((item, i) => (
                            <li key={i} className="text-gray-700">{item}</li>
                          ))}
                        </ul>
                      );
                    case 'code':
                      return (
                        <pre key={block.id} className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto mb-4 text-sm">
                          <code>{block.content}</code>
                        </pre>
                      );
                    case 'quote':
                      return (
                        <blockquote key={block.id} className="border-l-4 border-blue-500 bg-blue-50 pl-4 pr-4 py-3 italic text-gray-600 mb-4 rounded-r-lg">
                          {block.content}
                        </blockquote>
                      );
                    case 'divider':
                      return <hr key={block.id} className="my-8 border-gray-300" />;
                    case 'table':
                      return block.tableData ? (
                        <div key={block.id} className="overflow-x-auto mb-4">
                          <table className="min-w-full border-collapse border border-gray-300 rounded-lg">
                            <tbody>
                              {block.tableData.map((row, rowIndex) => (
                                <tr key={rowIndex} className={rowIndex === 0 ? 'bg-gray-100' : 'bg-white'}>
                                  {row.map((cell, colIndex) => (
                                    <td
                                      key={colIndex}
                                      className={`border border-gray-300 px-4 py-2 ${
                                        rowIndex === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'
                                      }`}
                                    >
                                      {cell.content}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null;
                    case 'image':
                      return block.imageData?.src ? (
                        <figure key={block.id} className={`mb-4 ${
                          block.imageData.alignment === 'left' ? 'text-left' :
                          block.imageData.alignment === 'right' ? 'text-right' :
                          block.imageData.alignment === 'full' ? '' : 'text-center'
                        }`}>
                          <img
                            src={block.imageData.src}
                            alt={block.imageData.alt || ''}
                            className={`rounded-lg ${
                              block.imageData.alignment === 'full' ? 'w-full' : 'max-w-md inline-block'
                            }`}
                          />
                          {block.imageData.caption && (
                            <figcaption className="text-sm text-gray-500 mt-2 italic">
                              {block.imageData.caption}
                            </figcaption>
                          )}
                        </figure>
                      ) : null;
                    default:
                      return null;
                  }
                })}
              </div>

              {/* Tags */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map(tag => (
                    <span key={tag} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex items-center">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleLike}
                    className={`flex items-center px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                      isLiked
                        ? 'bg-red-50 border-red-300 text-red-700'
                        : 'border-gray-200 text-gray-700 hover:bg-red-50 hover:border-red-300 hover:text-red-700'
                    }`}
                  >
                    <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} mr-2`}></i>
                    {isLiked ? 'Liked' : 'Like'}
                  </button>
                  <button className="flex items-center px-4 py-2 rounded-lg border-2 border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all duration-200">
                    <i className="ri-bookmark-line mr-2"></i>
                    Save
                  </button>
                  <button className="flex items-center px-4 py-2 rounded-lg border-2 border-gray-200 text-gray-700 hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-all duration-200">
                    <i className="ri-share-line mr-2"></i>
                    Share
                  </button>
                </div>
              </div>
            </article>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-all z-40"
        >
          <i className="ri-arrow-up-line text-xl"></i>
        </button>
      )}
    </div>
  );
}
